import React, { useState, useEffect } from 'react';
import { Calendar, Hash, Type, CheckSquare, Clock } from 'lucide-react';

/**
 * ReportParameters Component
 * Displays and collects parameter inputs for report execution
 */
const ReportParameters = ({ parameters = [], values = {}, onChange, onExecute }) => {
  const [paramValues, setParamValues] = useState(values);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setParamValues(values);
  }, [values]);

  const handleChange = (paramName, value) => {
    const newValues = {
      ...paramValues,
      [paramName]: value
    };
    setParamValues(newValues);
    
    // Clear error for this parameter
    if (errors[paramName]) {
      setErrors({ ...errors, [paramName]: null });
    }

    // Notify parent
    if (onChange) {
      onChange(newValues);
    }
  };

  const validateParameters = () => {
    const newErrors = {};
    let isValid = true;

    parameters.forEach(param => {
      const value = paramValues[param.name];
      
      // Check required parameters
      if (param.required && (value === null || value === undefined || value === '')) {
        newErrors[param.name] = `${param.label || param.name} is required`;
        isValid = false;
      }

      // Type validation
      if (value !== null && value !== undefined && value !== '') {
        switch (param.type) {
          case 'integer':
            if (isNaN(parseInt(value, 10))) {
              newErrors[param.name] = 'Must be a valid integer';
              isValid = false;
            }
            break;
          case 'decimal':
            if (isNaN(parseFloat(value))) {
              newErrors[param.name] = 'Must be a valid number';
              isValid = false;
            }
            break;
          case 'date':
          case 'datetime':
            const date = new Date(value);
            if (isNaN(date.getTime())) {
              newErrors[param.name] = 'Must be a valid date';
              isValid = false;
            }
            break;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleExecute = () => {
    if (validateParameters()) {
      if (onExecute) {
        onExecute(paramValues);
      }
    }
  };

  const renderParameterInput = (param) => {
    const value = paramValues[param.name] || param.defaultValue || '';
    const hasError = !!errors[param.name];

    const commonClasses = `w-full px-2 py-1 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 border rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent ${
      hasError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
    }`;

    const getIcon = () => {
      switch (param.type) {
        case 'integer':
        case 'decimal':
          return <Hash className="w-4 h-4 text-gray-400" />;
        case 'date':
        case 'datetime':
          return <Calendar className="w-4 h-4 text-gray-400" />;
        case 'boolean':
          return <CheckSquare className="w-4 h-4 text-gray-400" />;
        case 'time':
          return <Clock className="w-4 h-4 text-gray-400" />;
        default:
          return <Type className="w-4 h-4 text-gray-400" />;
      }
    };

    switch (param.type) {
      case 'integer':
      case 'decimal':
        return (
          <input
            type="number"
            step={param.type === 'decimal' ? '0.01' : '1'}
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            placeholder={param.placeholder || param.label}
            className={commonClasses}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            className={commonClasses}
          />
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            className={commonClasses}
          />
        );

      case 'time':
        return (
          <input
            type="time"
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            className={commonClasses}
          />
        );

      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleChange(param.name, e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-xs text-gray-900 dark:text-gray-100">{param.description}</span>
          </div>
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            className={commonClasses}
          >
            {!param.required && <option value="">-- Select --</option>}
            {param.options && param.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label || option.value}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            placeholder={param.placeholder || param.label}
            rows={3}
            className={commonClasses}
          />
        );

      default: // string
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            placeholder={param.placeholder || param.label}
            className={commonClasses}
          />
        );
    }
  };

  if (!parameters || parameters.length === 0) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Parameters</h3>
        {onExecute && (
          <button
            onClick={handleExecute}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Run Report
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {parameters.map((param) => (
          <div key={param.name} className="space-y-1">
            <label className="block">
              <div className="flex items-center space-x-1 mb-1">
                <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
                  {param.label || param.name}
                  {param.required && <span className="text-red-500 ml-0.5">*</span>}
                </span>
              </div>
              {param.description && param.type !== 'boolean' && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{param.description}</p>
              )}
              {renderParameterInput(param)}
              {errors[param.name] && (
                <p className="text-xs text-red-500 mt-0.5">{errors[param.name]}</p>
              )}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportParameters;

